import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../libs/shared/core/src/services/tracking/tracking';
import {DropdownItem} from '../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemMultiline} from '../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemMultiline';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../libs/shared/ui/src/styles/mixins';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {isContactACustomChannel} from '../../../helpers/models/contactHelpers';
import {AccountModel} from '../../../models/api/accountModel';
import {VisualContactModel} from '../../../models/api/visualContactModel';
import {DropdownItemContactAvatar} from '../../common/dropdowns/dropdownItemContactAvatar';
import {DropdownItemContactWithAccountAvatar} from '../../common/dropdowns/dropdownItemContactWithAccountAvatar';
import {ContactDetailsVersionsEnum, useContactDetailsContext} from '../contactDetailsContext';
import {buildSwitchContactsPayload} from '../contactDetailsTrackingHelpers';

/*
 * Props.
 */

interface ContactPluginHeaderContactProps {
  isSelected: boolean;
  contact: VisualContactModel;
  account: AccountModel | undefined;
  isTeammate?: boolean;
  onSelectedContactChange: (model: VisualContactModel) => void;
}

interface StyledContactNameDivProps {
  $isSelected: boolean;
}

/*
 * Styles.
 */

const StyledDropdownItem = styled(DropdownItem)`
  display: flex;
  flex-flow: row;
  height: 48px;
`;

const StyledDropdownDetailContainerDiv = styled.div`
  flex: 0 0 40px;
  position: relative;
`;

const StyledDropdownContactContainerDiv = styled.div`
  margin-left: 12px;
  align-self: center;
`;

const StyledContactInformationContainerDiv = styled.div`
  flex: 1;
  overflow: hidden;
`;

const StyledDropdownSpacerDiv = styled.div`
  margin-left: 12px;
`;

const StyledContactNameDiv = styled.div<StyledContactNameDivProps>`
  color: ${(p) => p.theme.greys.shade80};
  line-height: 20px;
  font-weight: ${fontWeights.medium};
  ${ellipsis()};

  ${(p) =>
    p.$isSelected &&
    css`
      font-weight: ${fontWeights.bold};
    `}
`;

const StyledSelectedIcon = styled(Icon)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  margin: auto;
`;

const StyledTooltipContainerDiv = styled.div`
  font-size: ${fontSizes.verySmall};
`;

const StyledTooltipTitleDiv = styled.div`
  font-weight: ${fontWeights.bold};
`;

/*
 * Component.
 */

export const ContactPluginHeaderContact: FC<ContactPluginHeaderContactProps> = (props) => {
  const theme = useUserTheme();
  const {contact, isSelected, account, isTeammate, onSelectedContactChange} = props;
  const isContactSourceCustomChannel = isContactACustomChannel(contact.contact);
  const {version} = useContactDetailsContext();

  const contactName = renderContactName(contact, version, account, isTeammate);
  const contactHandle = isContactSourceCustomChannel ? (
    <Trans id="contact-details-header-custom-channel">Custom channel</Trans>
  ) : (
    contact.contact.handle
  );

  return (
    <StyledDropdownItem
      key={contact.contact.uid}
      onSelect={() => {
        onSelectedContactChange(contact);
        trackInterfaceEvent(
          InterfaceEventsEnum.P4_CONTACT_DETAILS_SWITCH_CONTACT,
          buildSwitchContactsPayload(contact.namespace, Boolean(isTeammate)),
        );
      }}
      interactionId="contact-details-plugin-header-contact"
    >
      <StyledDropdownContactContainerDiv>
        {isTeammate ? (
          <DropdownItemContactAvatar contact={contact} />
        ) : (
          <DropdownItemContactWithAccountAvatar account={account} contact={contact} />
        )}
      </StyledDropdownContactContainerDiv>
      <StyledContactInformationContainerDiv>
        <TooltipCoordinator
          render={() => (
            <StyledTooltipContainerDiv>
              <StyledTooltipTitleDiv>{contactName}</StyledTooltipTitleDiv>
              {contactHandle}
            </StyledTooltipContainerDiv>
          )}
        >
          <DropdownItemMultiline
            title={
              <TooltipOverflowCondition>
                <StyledContactNameDiv $isSelected={isSelected}>{contactName}</StyledContactNameDiv>
              </TooltipOverflowCondition>
            }
            subtitle={<TooltipOverflowCondition>{contactHandle}</TooltipOverflowCondition>}
          />
        </TooltipCoordinator>
      </StyledContactInformationContainerDiv>
      {isSelected ? (
        <StyledDropdownDetailContainerDiv>
          <StyledSelectedIcon name="checkmarkSelectedSmall" color={theme.palette.blue.shade40} />
        </StyledDropdownDetailContainerDiv>
      ) : (
        <StyledDropdownSpacerDiv />
      )}
    </StyledDropdownItem>
  );
};

/*
 * Helpers.
 */

const renderContactName = (
  contact: VisualContactModel,
  version: ContactDetailsVersionsEnum,
  account: AccountModel | undefined,
  isTeammate?: boolean,
) => {
  if (version === ContactDetailsVersionsEnum.VERSION_1 || isTeammate) {
    return contact.name;
  }
  return `${contact.name}${account ? ` · ${account.name}` : ''}`;
};
